/**
 * Customer Relationships section for the supplier settings tab.
 * Manages per-site settings including supplier codes and currency.
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Handshake, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { SupplierCustomerRelationship, Site } from '../../lib/api';

interface CustomerRelationshipsSectionProps {
  relationships: SupplierCustomerRelationship[];
  relLoading: boolean;
  expandedRelId: number | null;
  setExpandedRelId: (id: number | null) => void;
  relSaving: number | null;
  canEditSettings: boolean;
  assignDialogOpen: boolean;
  setAssignDialogOpen: (open: boolean) => void;
  availableSites: Site[];
  selectedSiteId: number | null;
  setSelectedSiteId: (id: number | null) => void;
  assigning: boolean;
  setRelationships: React.Dispatch<React.SetStateAction<SupplierCustomerRelationship[]>>;
  updateRelationship: (id: number, data: Partial<SupplierCustomerRelationship>) => void;
  removeRelationship: (id: number) => void;
  openAssignDialog: () => void;
  assignSite: () => void;
}

export function CustomerRelationshipsSection({
  relationships, relLoading, expandedRelId, setExpandedRelId,
  relSaving, canEditSettings,
  assignDialogOpen, setAssignDialogOpen, availableSites,
  selectedSiteId, setSelectedSiteId, assigning,
  setRelationships, updateRelationship, removeRelationship,
  openAssignDialog, assignSite,
}: CustomerRelationshipsSectionProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><Handshake className="h-5 w-5" />Customer Relationships</CardTitle>
          <CardDescription>Per-company settings including supplier codes and currency</CardDescription>
        </div>
        {canEditSettings && (<Button variant="outline" size="sm" onClick={openAssignDialog}>+ Assign Customer</Button>)}
      </CardHeader>
      <CardContent>
        {relLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : relationships.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Handshake className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No company relationships found</p>
            {canEditSettings && <p className="text-sm mt-1">Click "Assign Customer" to add this supplier to a customer company</p>}
          </div>
        ) : (
          <div className="space-y-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Supplier Code</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Call-Off</TableHead>
                  <TableHead>Buyer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {relationships.map((rel) => {
                  const isExpanded = expandedRelId === rel.id;
                  return (
                    <>
                      <TableRow key={`rel-${rel.id}`} className="cursor-pointer hover:bg-muted/50" onClick={() => setExpandedRelId(isExpanded ? null : rel.id)}>
                        <TableCell>{isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                        <TableCell className="font-medium">{rel.customer_name}</TableCell>
                        <TableCell>{rel.supplier_code || '-'}</TableCell>
                        <TableCell>{rel.currency || '-'}</TableCell>
                        <TableCell>
                          <Badge className={rel.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800'}>
                            {rel.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={rel.call_off_enabled ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-muted text-muted-foreground'}>
                            {rel.call_off_enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell>{rel.buyer_name || <span className="text-muted-foreground italic">Not assigned</span>}</TableCell>
                      </TableRow>

                      {isExpanded && (
                        <TableRow key={`rel-detail-${rel.id}`}>
                          <TableCell colSpan={7} className="bg-muted/20 p-0">
                            <div className="px-8 py-5 space-y-5">
                              {/* Codes & Currency */}
                              <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-1.5">
                                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Supplier Code</label>
                                  <input type="text" value={rel.supplier_code} onChange={(e) => setRelationships((prev) => prev.map((r) => r.id === rel.id ? { ...r, supplier_code: e.target.value } : r))} onBlur={() => updateRelationship(rel.id, { supplier_code: rel.supplier_code })} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings} />
                                </div>
                                <div className="space-y-1.5">
                                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Currency</label>
                                  <select value={rel.currency} onChange={(e) => updateRelationship(rel.id, { currency: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings}>
                                    <option value="">Not set</option>
                                    {['GBP', 'USD', 'EUR', 'PLN', 'CAD', 'AUD', 'CHF', 'SEK', 'NOK', 'DKK', 'CZK', 'JPY', 'CNY', 'INR'].map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                </div>
                              </div>

                              {/* Buyer */}
                              <div>
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-2">Assigned Personnel</label>
                                <div className="grid gap-3 md:grid-cols-3 text-sm">
                                  <div className="flex items-center gap-2 p-2 border rounded-md">
                                    <Users className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">Buyer</p>
                                      <p className="font-medium">{rel.buyer_name || 'Not assigned'}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center justify-between">
                                {relSaving === rel.id && (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Saving...</div>
                                )}
                                {canEditSettings && (
                                  <Button variant="destructive" size="sm" className="ml-auto" onClick={(e) => { e.stopPropagation(); removeRelationship(rel.id); }}>Remove Customer</Button>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Assign Customer Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Customer</DialogTitle>
            <DialogDescription>Select a customer company to assign this supplier to.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {availableSites.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No available customers to assign</p>
            ) : (
              <select value={selectedSiteId ?? ''} onChange={(e) => setSelectedSiteId(e.target.value ? parseInt(e.target.value) : null)} className="w-full px-3 py-2 border rounded-md text-sm bg-background">
                <option value="">Select a company...</option>
                {availableSites.map((site) => (<option key={site.id} value={site.id}>{site.name} ({site.code})</option>))}
              </select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={assignSite} disabled={!selectedSiteId || assigning}>
              {assigning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
